import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostBinding,
  HostListener,
  Input,
  NgModule,
  OnDestroy,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DomSanitizer } from '@angular/platform-browser';
import { ContactModalComponent, ContactTypeEnum } from '@commons/contact-modal/contact-modal.component';
import { BreakpointsService } from '@core/services/breakpoints/breakpoints.service';
import { environment } from '@environment';
import { UntilDestroy } from '@ngneat/until-destroy';
import { SharedModule } from '@shared/shared.module';
import { ImaginarySrcModule } from '@wizbii/angular-utilities';
import { trackConversion, trackEventWithAtInternet } from '@wizbii/tracking';
import { BehaviorSubject, Observable } from 'rxjs';
import { take } from 'rxjs/operators';

export interface CardContent {
  title: string;
  img: string;
  description?: string;
  link?: string[];
  url?: string;
  modalContactType?: ContactTypeEnum;
  dropdown?: DropDownItem[];
  trackEventAction?: string;
  trackEventLabel?: string;
  gtag?: string;
}

export interface DropDownItem {
  title: string;
  url?: string;
  link?: string[];
  queryParams?: unknown;
  trackEventAction?: string;
}

@UntilDestroy()
@Component({
  selector: 'app-card',
  templateUrl: './card.component.html',
  styleUrls: ['./card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CardComponent implements OnDestroy {
  @Input() cardContent!: CardContent;
  @Input() isSmall = false;
  @Input() accentColor = '#526ab6';

  displayDropDown$ = new BehaviorSubject<boolean>(false);

  trackEvent = trackEventWithAtInternet;

  get isMobile$(): Observable<boolean> {
    return this.breakpointsService.isMobile$;
  }

  constructor(
    private readonly breakpointsService: BreakpointsService,
    private readonly elm: ElementRef,
    private readonly dialog: MatDialog,
    private readonly sanitizer: DomSanitizer
  ) {}

  openModal(): void {
    this.isMobile$.pipe(take(1)).subscribe({
      next: (isMobile) => {
        this.dialog.open(ContactModalComponent, {
          panelClass: isMobile ? 'wizbii-mobile-panel--white' : 'wizbii-panel--white',
          backdropClass: 'wzb-backdrop',
          maxWidth: isMobile ? '100vw' : '',
          autoFocus: false,
          data: {
            contactType: this.cardContent.modalContactType,
            isMobile,
          },
          disableClose: isMobile,
          position: isMobile ? { bottom: '0' } : {},
        });
      },
    });
  }

  trackByDropDownItem(_: number, item: DropDownItem): string {
    return item.title;
  }

  ngOnDestroy(): void {
    this.displayDropDown$.complete();
  }

  trackOnClick(): void {
    if (
      // tslint:disable-next-line: no-boolean-literal-compare
      this.cardContent.gtag &&
      environment.platform === 'prod'
    ) {
      trackConversion({
        allow_custom_scripts: true,
        send_to: this.cardContent.gtag,
      });
    }

    if (this.cardContent.trackEventAction) {
      this.trackEvent({
        category: 'Home',
        action: `Click ${this.cardContent.trackEventAction}`,
        type: 'navigation',
        ...(this.cardContent.trackEventLabel ? { label: this.cardContent.trackEventLabel } : {}),
      });
    }
  }

  click(event: MouseEvent): void {
    if (this.cardContent.link || this.cardContent.url) {
      const clonedEvent = new MouseEvent(event.type, event);
      this.elm.nativeElement.querySelector('.card__btn').dispatchEvent(clonedEvent);
    } else if (this.cardContent.modalContactType) {
      this.openModal();
    } else {
      this.displayDropDown$.next(!this.displayDropDown$.value);
    }
  }

  @HostBinding('attr.style')
  public get valueAsStyle(): any {
    return this.sanitizer.bypassSecurityTrustStyle(`--accent-color: ${this.accentColor}`);
  }

  @HostListener('keydown.enter', ['$event.target'])
  @HostListener('click', ['$event.target', '$event'])
  doRegister(target: any, event: MouseEvent): void {
    if (['BUTTON', 'A'].indexOf(target.tagName) !== -1) {
      return;
    }

    this.click(event);
  }

  dropDownItemClicked(eventAction: string): void {
    if (eventAction) {
      this.trackEvent({ category: 'Home', action: `Click ${eventAction}`, type: 'navigation' });
    }
  }

  onClickedDropdown(): void {
    this.displayDropDown$.next(!this.displayDropDown$.value);
  }
}

@NgModule({
  imports: [SharedModule, ImaginarySrcModule],
  declarations: [CardComponent],
  exports: [CardComponent],
})
export class CardModule {}
